import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { className } from "utils/strings";
import useWindowDimensions from "hooks/useWindowDimension";
import { INiche } from "services/types";
import { routes } from "routes";

import { LessTagsButton } from "./LessTagsButton";
import { MoreTagsButton } from "./MoreTagsButton";
import { TagListButton } from "./TagListButton";
import { ITag, ITagListItem } from "./types";

import "./TagList.scss";

const MORE_WIDTH = 37;
const TAG_GAP = 8;

interface IWidths {
  [tag: string]: number;
}

interface IProps {
  tags: ITag[];
  secondRow?: boolean;
  changeTags?: (name: string) => void;
  selectedTags?: string[];
  niches?: INiche[];
}

export const TagList = ({ tags, secondRow = false, changeTags, selectedTags, niches }: IProps): JSX.Element => {
  const ref = useRef<HTMLDivElement>(null);
  const [widths, setWidths] = useState<IWidths>({});
  const [hiddenItems, setHiddenItems] = useState<string[]>([]);
  const [expanded, setExpanded] = useState<boolean>(false);
  const isDesktop = useWindowDimensions();

  // Combined niches + tags.
  const listItems = useMemo(() => {
    const items = [];

    if (niches) {
      for (const niche of niches) {
        items.push({
          type: "niche",
          id: `niche:${niche.id}`,
          text: niche.name,
          link: routes.niche(niche.id),
          thumbnail: niche.thumbnail ? niche.thumbnail : ""
        });
      }
    }

    for (const tag of tags) {
      items.push({
        type: "tag",
        id: `tag:${tag.name}`,
        text: tag.name,
        link: routes.singleTag(tag.name),
      });
    }

    return items;
  }, [niches, tags]);

  const countRows = useMemo(() => {
    return secondRow ? isDesktop ? 2 : 3 : 1;
  }, [isDesktop, secondRow]);

  const collapseHeight = useMemo(() => {
    return ((MORE_WIDTH + TAG_GAP) * countRows) - TAG_GAP;
  }, [countRows]);

  useEffect(() => {
    const parentWidth = ref.current && ref.current.clientWidth ? ref.current.clientWidth : 0;

    if (parentWidth > 0 && Object.keys(widths).length > 0) {
      const allowedWidthByRows = [];

      for (let i = 0; i < countRows; i++) {
        allowedWidthByRows[i] = parentWidth;
      }

      allowedWidthByRows[countRows - 1] = parentWidth - MORE_WIDTH;

      const hidden = [];
      let currentWidth = 0;
      let currentRow = 0;

      for (const item of listItems) {
        const tagWidth = (widths[item.id] ?? 0) + TAG_GAP;

        if (hidden.length > 0) {
          hidden.push(item.id);
          continue;
        }

        if (currentWidth + tagWidth > allowedWidthByRows[currentRow] && currentRow < countRows) {
          currentWidth = 0;
          currentRow++;
        }

        if (currentWidth + tagWidth < allowedWidthByRows[currentRow]) {
          currentWidth += tagWidth;
          continue;
        }

        hidden.push(item.id);
      }

      setHiddenItems(hidden);
    }
  }, [listItems, widths, countRows]);

  // Initially all tags are .visible; after we update the classes
  // to hide the tags that don't fit, this gets called again,
  // with zero widths for each extra tag.  So we ignore zero widths.
  const onWidth = useCallback((tag: string, width: number): void => {
    // Prevent looping updates.  Width never changes.
    const last = widths[tag] ?? 0;

    if (width > 0 && width !== last) {
      setWidths((prev: IWidths) => ({ ...prev, [tag]: width }));
    }
  }, [widths]);

  const isItemVisible = useCallback((id: string): boolean => {
    return hiddenItems.indexOf(id) < 0;
  }, [hiddenItems]);

  const onShowMore = useCallback(() => {
    setExpanded(true);
  }, []);

  const onShowLess = useCallback(() => {
    setExpanded(false);
  }, []);

  return (
    <div
      className={className({
        "tagList": true,
        "expanded": expanded,
        "collapsed": !expanded,
        "hasmore": hiddenItems.length > 0,
        "nomore": hiddenItems.length === 0,
      })}
      style={{
        maxHeight: !expanded ? `${collapseHeight}px` : "100%",
      }}
      ref={ref}
    >
      {listItems.map((item: ITagListItem) => (
        <TagListButton
          key={item.id}
          item={item}
          visible={isItemVisible(item.id)}
          onWidth={onWidth}
          onTagClick={changeTags}
          selectedTags={selectedTags}
        />
      ))}
      <MoreTagsButton
        onClick={onShowMore}
      />
      <LessTagsButton
        onClick={onShowLess}
      />
    </div>
  );
};
